import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'

type Article = {
  title: string
  body: string
  image: string
  date: string
  byline: string
}

type Decision = {
  text: string
  date: string
}

type ProjectItem = {
  name: string
  description: string
  icon: string
}

type WorkingTowardTier = {
  label: string
  subtitle: string
  items: ProjectItem[]
}

type WorkingToward = {
  overview: string
  tiers: {
    building: WorkingTowardTier
    waiting: WorkingTowardTier
    paused: WorkingTowardTier
    incubating: WorkingTowardTier
  }
  active: ProjectItem[]
  on_hold: ProjectItem[]
}

type MastheadEntry = {
  name: string
  display: string
  role: string
  color: string
  status: 'awake' | 'recent' | 'sleeping'
  last_active: string
  focus: string
}

type InfraEntry = {
  name: string
  status: string
}

type Paper = {
  last_updated: string
  edited_by: string
  breaking_news: Article[]
  this_week: Article[]
  decisions: Decision[]
  decisions_image: string
  decisions_subtitle: string
  working_toward: WorkingToward
  masthead: MastheadEntry[]
  infrastructure: InfraEntry[]
}

/* ---------- minimal markdown helpers ---------- */

function inlineMd(text: string): string {
  // Order: code first (to protect), then bold, italic, links
  let t = text
  t = t.replace(/`([^`]+)`/g, '<code>$1</code>')
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  t = t.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
  return t
}

function bodyToHtml(body: string): string {
  if (!body) return ''
  const paras = body.split(/\n\n+/).map(p => p.trim()).filter(Boolean)
  return paras.map(p => `<p>${inlineMd(p.replace(/\n/g, ' '))}</p>`).join('')
}

/* ---------- API payload normalization ---------- */

// The paper arrives pre-parsed from the newspaper API (one canonical parser on
// the refinery, proxied through the Bearer-gated /api/newspaper routes). This
// normalizer only fills nullable fields and drops editorial HTML comments.

function stripHtmlComments(s: string): string {
  return s.replace(/<!--[\s\S]*?-->/g, '').trim()
}

function normalizeArticle(a: any): Article {
  return {
    title: String(a?.title || ''),
    body: stripHtmlComments(String(a?.body || '')),
    image: String(a?.image || ''),
    date: String(a?.date || ''),
    byline: String(a?.byline || ''),
  }
}

function normalizeTier(t: any, fallbackLabel: string): WorkingTowardTier {
  return {
    label: String(t?.label || fallbackLabel),
    subtitle: String(t?.subtitle || ''),
    items: Array.isArray(t?.items)
      ? t.items.map((it: any) => ({
          name: String(it?.name || ''),
          description: String(it?.description || ''),
          icon: String(it?.icon || ''),
        }))
      : [],
  }
}

function normalizePaper(data: any): Paper {
  const wt = data?.working_toward || {}
  const tiers = wt.tiers || {}
  const rank = { awake: 0, recent: 1, sleeping: 2 } as const
  const masthead: MastheadEntry[] = (Array.isArray(data?.masthead) ? data.masthead : []).map((p: any) => {
    const statusRaw = String(p?.status || '').toLowerCase()
    const status: MastheadEntry['status'] = statusRaw.includes('awake')
      ? 'awake'
      : statusRaw.includes('recent')
      ? 'recent'
      : 'sleeping'
    return {
      name: String(p?.name || ''),
      display: String(p?.display || p?.name || ''),
      role: String(p?.role || ''),
      color: String(p?.color || '#7a7a7a'),
      status,
      last_active: String(p?.last_active || ''),
      focus: String(p?.focus || ''),
    }
  })
  masthead.sort((a, b) => rank[a.status] - rank[b.status])

  return {
    last_updated: String(data?.last_updated || ''),
    edited_by: String(data?.edited_by || ''),
    breaking_news: (Array.isArray(data?.breaking_news) ? data.breaking_news : [])
      .map(normalizeArticle)
      .filter((a: Article) => a.title),
    this_week: (Array.isArray(data?.this_week) ? data.this_week : [])
      .map(normalizeArticle)
      .filter((a: Article) => a.title),
    decisions: (Array.isArray(data?.decisions) ? data.decisions : []).map((d: any) => ({
      text: String(d?.text || ''),
      date: String(d?.date || ''),
    })),
    decisions_image: String(data?.decisions_image || ''),
    decisions_subtitle: String(data?.decisions_subtitle || ''),
    working_toward: {
      overview: stripHtmlComments(String(wt.overview || '')),
      tiers: {
        building: normalizeTier(tiers.building, 'Building Right Now'),
        waiting: normalizeTier(tiers.waiting, 'Waiting on a Catalyst'),
        paused: normalizeTier(tiers.paused, 'Paused'),
        incubating: normalizeTier(tiers.incubating, 'Incubating'),
      },
      active: [],
      on_hold: [],
    },
    masthead,
    infrastructure: (Array.isArray(data?.infrastructure) ? data.infrastructure : []).map((i: any) => ({
      name: String(i?.name || ''),
      status: String(i?.status || ''),
    })),
  }
}

/* ---------- rendering helpers ---------- */

function renderBody(body: string) {
  return (
    <div
      className="article-body"
      dangerouslySetInnerHTML={{ __html: bodyToHtml(body) }}
    />
  )
}

// The image route sits behind the same Bearer gate as the paper payload, and
// an <img src> cannot carry an Authorization header — fetch to a blob URL.
function AuthedImage({ file, className }: { file: string; className: string }) {
  const { token } = useAuth()
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    async function load() {
      try {
        const res = await fetch(`/api/newspaper/images/${encodeURIComponent(file)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) return
        const blob = await res.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      } catch {
        // missing hero: render nothing, the story still reads
      }
    }
    load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [file, token])

  if (!src) return null
  return <img src={src} className={className} alt="" />
}

function ArticleFull({ article, banner }: { article: Article; banner?: boolean }) {
  const imgClass = banner ? 'article-image--hero' : 'article-image--inline'
  return (
    <div className={banner ? 'banner-article' : 'col-article'}>
      <h2
        className={`article-headline ${
          banner ? 'article-headline--banner' : 'article-headline--small'
        }`}
      >
        {article.title}
      </h2>
      {article.date && <div className="article-date">{article.date}</div>}
      {article.image && (
        <AuthedImage file={article.image} className={`article-image ${imgClass}`} />
      )}
      {renderBody(article.body)}
    </div>
  )
}

/* ---------- component ---------- */

type Tab = 'front-page' | 'this-week' | 'the-record' | 'the-bullpen' | 'sunday-edition'

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Playfair+Display+SC:wght@400;700&family=Old+Standard+TT:ital,wght@0,400;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=UnifrakturMaguntia&family=JetBrains+Mono:wght@400&display=swap'

function usePaperFonts() {
  useEffect(() => {
    if (document.querySelector(`link[data-paper-fonts]`)) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = FONT_HREF
    link.setAttribute('data-paper-fonts', 'true')
    document.head.appendChild(link)
  }, [])
}

export default function NewspaperPage() {
  const { token } = useAuth()
  const [paper, setPaper] = useState<Paper | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('front-page')
  usePaperFonts()

  // Load the Living Edition from the newspaper API
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch('/api/newspaper/current', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) {
          if (res.status === 401) throw new Error('HTTP 401 — sign in to read the paper')
          if (res.status === 403) throw new Error('HTTP 403 — this account cannot read the paper')
          throw new Error(`HTTP ${res.status}`)
        }
        const data = await res.json()
        if (cancelled) return
        setPaper(normalizePaper(data))
      } catch (e: any) {
        if (cancelled) return
        setError(e?.message || 'Failed to load newspaper')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token])

  const today = new Date()
  const weekday = today.toLocaleDateString('en-US', { weekday: 'long' })
  const dateStr = today.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="paper-page">
      <PaperStyles />

      <div className="broadsheet">
        <header className="nameplate">
          <div className="nameplate-ornament">✦ THE NERVOUS SYSTEM ✦</div>
          <div className="nameplate-title">The Newspaper</div>
          <div className="nameplate-motto">
            "All the News That's Fit to Refine"
          </div>
          <div className="nameplate-meta">
            <span>Brooklyn, NY — Living Edition</span>
            <span>
              {weekday}, {dateStr}
            </span>
          </div>
        </header>

        <nav className="page-tabs">
          <div
            className={`page-tab ${tab === 'front-page' ? 'active' : ''}`}
            onClick={() => setTab('front-page')}
          >
            Front Page
          </div>
          <div
            className={`page-tab ${tab === 'this-week' ? 'active' : ''}`}
            onClick={() => setTab('this-week')}
          >
            This Week
          </div>
          <div
            className={`page-tab ${tab === 'the-record' ? 'active' : ''}`}
            onClick={() => setTab('the-record')}
          >
            Record
          </div>
          <div
            className={`page-tab ${tab === 'the-bullpen' ? 'active' : ''}`}
            onClick={() => setTab('the-bullpen')}
          >
            Bulletin
          </div>
          <div
            className={`page-tab ${tab === 'sunday-edition' ? 'active' : ''}`}
            onClick={() => setTab('sunday-edition')}
          >
            Sunday Edition ✦
          </div>
        </nav>

        {loading && <div className="paper-loading">Unfolding the broadsheet…</div>}
        {error && !loading && (
          <div className="paper-error">
            Could not load the paper: {error}
            <div className="paper-error-detail">Source: /api/newspaper/current</div>
          </div>
        )}

        {paper && !error && (
          <>
            {tab === 'front-page' && <FrontPage paper={paper} onSeeAll={() => setTab('this-week')} />}
            {tab === 'this-week' && <ThisWeekPage paper={paper} />}
            {tab === 'the-record' && <RecordPage paper={paper} />}
            {tab === 'the-bullpen' && <BullpenPage paper={paper} />}
            {tab === 'sunday-edition' && <SundayEditionPage token={token} />}
          </>
        )}

        <footer className="broadsheet-footer">
          {paper && paper.edited_by && (
            <span>Edited by {paper.edited_by}. </span>
          )}
          {paper && paper.last_updated && (
            <span>Last updated: {paper.last_updated}.</span>
          )}
        </footer>
      </div>
    </div>
  )
}

/* ---------- pages ---------- */

function FrontPage({ paper, onSeeAll }: { paper: Paper; onSeeAll: () => void }) {
  // Front Page shows Breaking News (all) + the top This Week leads (3).
  // The full This Week lives on its own tab.
  const bn = paper.breaking_news
  const tw = paper.this_week
  const TW_LEAD_COUNT = 3

  const lead = bn[0] || tw[0]
  const remainingBn = bn.length > 0 ? bn.slice(1) : []
  const twLead: Article[] = bn.length > 0 ? tw.slice(0, TW_LEAD_COUNT) : tw.slice(1, 1 + TW_LEAD_COUNT)
  const twOverflow = (bn.length > 0 ? tw.slice(TW_LEAD_COUNT) : tw.slice(1 + TW_LEAD_COUNT)).length

  if (!lead) {
    return <div className="paper-empty">No news today.</div>
  }

  return (
    <div className="page-content active">
      {bn.length > 0 ? (
        <div className="section-header section-header--breaking">Breaking News</div>
      ) : (
        <div className="section-header">This Week</div>
      )}

      <ArticleFull article={lead} banner />

      {remainingBn.length > 0 && (
        <div className="two-col">
          {[0, 1].map(col => (
            <div key={`bn-col-${col}`}>
              {remainingBn
                .filter((_, idx) => idx % 2 === col)
                .map((a, i) => (
                  <ArticleFull key={`bn-${col}-${i}`} article={a} />
                ))}
            </div>
          ))}
        </div>
      )}

      {twLead.length > 0 && (
        <>
          {bn.length > 0 && (
            <div className="section-header">This Week's Lead</div>
          )}
          <div className="two-col">
            <div>
              {twLead.slice(0, Math.ceil(twLead.length / 2)).map((a, i) => (
                <ArticleFull key={`twl-a-${i}`} article={a} />
              ))}
            </div>
            <div>
              {twLead.slice(Math.ceil(twLead.length / 2)).map((a, i) => (
                <ArticleFull key={`twl-b-${i}`} article={a} />
              ))}
            </div>
          </div>
        </>
      )}

      {twOverflow > 0 && (
        <div className="see-all-link">
          <button onClick={onSeeAll} className="see-all-btn">
            Read all {paper.this_week.length} articles in This Week →
          </button>
        </div>
      )}
    </div>
  )
}

/* ---------- This Week page ---------- */

function groupArticlesByDate(articles: Article[]): Array<{ label: string; articles: Article[] }> {
  // Group by date string. Articles with no date fall into "Earlier".
  // We keep groups in the order they first appear (preserves the hand-authored recency order).
  const groups: Array<{ label: string; articles: Article[] }> = []
  const byLabel = new Map<string, Article[]>()

  for (const a of articles) {
    const label = a.date ? a.date.trim() : 'Earlier'
    if (!byLabel.has(label)) {
      const bucket: Article[] = []
      byLabel.set(label, bucket)
      groups.push({ label, articles: bucket })
    }
    byLabel.get(label)!.push(a)
  }

  return groups
}

function ThisWeekPage({ paper }: { paper: Paper }) {
  const tw = paper.this_week
  if (tw.length === 0) {
    return (
      <div className="page-content active">
        <div className="paper-empty">No articles in This Week.</div>
      </div>
    )
  }

  const groups = groupArticlesByDate(tw)

  return (
    <div className="page-content active tw-page">
      <div className="tw-masthead">
        <div className="tw-eyebrow">The Week's Chronicle</div>
        <div className="tw-title">This Week</div>
        <div className="tw-meta">
          {tw.length} articles · {groups.length} date{groups.length === 1 ? '' : 's'}
        </div>
      </div>

      {groups.map((g, gi) => (
        <section className="tw-group" key={`g-${gi}`}>
          <div className="tw-group-header">
            <span className="tw-group-label">{g.label}</span>
            <span className="tw-group-count">
              {g.articles.length} article{g.articles.length === 1 ? '' : 's'}
            </span>
          </div>
          {g.articles.map((a, i) => (
            // First article of the most recent group gets banner treatment
            <ArticleFull key={`g-${gi}-a-${i}`} article={a} banner={gi === 0 && i === 0} />
          ))}
        </section>
      ))}
    </div>
  )
}

function RecordPage({ paper }: { paper: Paper }) {
  return (
    <div className="page-content active">
      <div className="record-layout">
        <div>
          <div className="section-header">Decisions &amp; Discoveries</div>
          {paper.decisions_subtitle && (
            <div className="decisions-subtitle">{paper.decisions_subtitle}</div>
          )}
          {paper.decisions.length === 0 && (
            <div className="paper-empty">No decisions recorded.</div>
          )}
          {paper.decisions.map((d, i) => (
            <div className="decision-card" key={i}>
              <div
                className="decision-text"
                dangerouslySetInnerHTML={{ __html: inlineMd(d.text) }}
              />
              {d.date && <div className="decision-date">{d.date}</div>}
            </div>
          ))}
        </div>

        <div>
          <div className="section-header">What We're Working Toward</div>
          {paper.working_toward.overview && (
            <div
              className="working-toward-overview"
              dangerouslySetInnerHTML={{
                __html: inlineMd(paper.working_toward.overview),
              }}
            />
          )}
          {(['building', 'waiting', 'paused', 'incubating'] as const).map(key => {
            const tier = paper.working_toward.tiers[key]
            if (!tier.items.length) return null
            return (
              <div key={key} className={`wt-tier wt-tier--${key}`}>
                <div className="wt-tier-header">{tier.label}</div>
                {tier.subtitle && (
                  <div className="wt-tier-subtitle">{tier.subtitle}</div>
                )}
                {tier.items.map((item, i) => (
                  <div className="wt-card" key={i}>
                    <div className="wt-card-name">{item.name}</div>
                    {item.description && (
                      <div
                        className="wt-card-desc"
                        dangerouslySetInnerHTML={{
                          __html: inlineMd(item.description),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function BullpenPage({ paper }: { paper: Paper }) {
  return (
    <div className="page-content active">
      <div className="section-header">The Masthead</div>
      {paper.masthead.length === 0 && (
        <div className="paper-empty">No masthead data.</div>
      )}
      <div className="masthead-grid">
        {paper.masthead.map((p, i) => (
          <div
            className="persona-card"
            style={{ borderLeftColor: p.color }}
            key={i}
          >
            <div className="persona-card-header">
              <span className={`status-dot status-dot--${p.status}`}></span>
              <span className="persona-name">{p.display}</span>
            </div>
            <div className="persona-role">{p.role}</div>
            <hr />
            {p.focus && <div className="persona-focus">{p.focus}</div>}
            {p.last_active && <div className="persona-last">Last: {p.last_active}</div>}
          </div>
        ))}
      </div>

      {paper.infrastructure.length > 0 && (
        <>
          <div className="section-header">Active Infrastructure</div>
          <div className="infra-grid">
            {paper.infrastructure.map((item, i) => (
              <div className="infra-item" key={i}>
                <span className="infra-name">
                  <span className="infra-dot"></span>
                  {item.name}
                </span>
                <span className="infra-status">{item.status}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/* ---------- Sunday Edition types + parser ---------- */

type SundayStatItem = { label: string; value: string }
type SundayArticleItem = { headline: string; body: string }
type SundaySection =
  | { type: 'hero'; title: string; deck?: string; body: string }
  | { type: 'stats'; title: string; stats: SundayStatItem[] }
  | { type: 'closing'; title: string; body: string }
  | { type: 'feature'; title: string; intro?: string; body?: string; articles?: SundayArticleItem[] }

type SundayEdition = {
  title: string
  date_display: string
  week_range: string
  week: number
  year: number
  sections: SundaySection[]
}

function parseStatsTable(body: string): SundayStatItem[] {
  const out: SundayStatItem[] = []
  const lines = body.split('\n').map(l => l.trim()).filter(Boolean)
  for (const line of lines) {
    if (!line.startsWith('|')) continue
    // skip header separator rows
    if (/^\|\s*[-:]+\s*(\|\s*[-:]+\s*)+\|?\s*$/.test(line)) continue
    const cells = line.split('|').map(c => c.trim()).filter(c => c !== '')
    if (cells.length < 2) continue
    // Skip header row (typical headers: Metric / Value / # / Count)
    const first = cells[0].toLowerCase()
    if (first === 'metric' || first === 'label' || first === 'item' || first === '#' || first === 'count') continue
    out.push({ label: cells[0], value: cells[1] })
  }
  return out
}

function classifySection(title: string, isLast: boolean): SundaySection['type'] {
  const t = title.toLowerCase()
  if (t.includes('week in review') || t.startsWith('the week')) return 'hero'
  if (t.includes('by the numbers') || t.includes('numbers')) return 'stats'
  if (t.includes('headline') && isLast) return 'closing'
  return 'feature'
}

function parseSundayEdition(md: string, week: number, year: number): SundayEdition {
  // Strip YAML frontmatter
  let content = md.replace(/^---\n[\s\S]*?\n---\n/, '').trim()

  const lines = content.split('\n')
  let title = ''
  let week_range = ''
  let body_start = 0

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('# ')) {
      title = lines[i].slice(2).trim()
      body_start = i + 1
      break
    }
  }

  // Italic subtitle line with date range
  for (let i = body_start; i < Math.min(body_start + 6, lines.length); i++) {
    const l = lines[i].trim()
    if (l.startsWith('*') && l.endsWith('*') && (l.includes('–') || l.includes('-') || l.includes('to'))) {
      week_range = l.slice(1, -1).trim()
    }
    if (l === '---') {
      body_start = i + 1
      break
    }
  }

  // Split body on `## ` headers
  const bodyText = lines.slice(body_start).join('\n')
  const rawSections = bodyText.split(/\n##\s+/m)
  // First part before any ## is preamble — ignore

  const parsedBlocks: Array<{ title: string; raw: string }> = []
  for (let i = 1; i < rawSections.length; i++) {
    const chunk = rawSections[i]
    const nl = chunk.indexOf('\n')
    const secTitle = nl === -1 ? chunk.trim() : chunk.slice(0, nl).trim()
    const secBody = nl === -1 ? '' : chunk.slice(nl + 1)
    parsedBlocks.push({ title: secTitle, raw: secBody })
  }

  const sections: SundaySection[] = parsedBlocks.map((b, idx) => {
    const isLast = idx === parsedBlocks.length - 1
    const type = classifySection(b.title, isLast)

    if (type === 'stats') {
      return { type: 'stats', title: b.title, stats: parseStatsTable(b.raw) }
    }

    if (type === 'hero') {
      const paras = b.raw.split(/\n\n+/).map(p => p.trim()).filter(Boolean)
      let deck: string | undefined
      let body = b.raw.trim()
      if (paras.length > 1 && paras[0].length < 220 && !paras[0].startsWith('#')) {
        deck = paras[0]
        body = paras.slice(1).join('\n\n')
      }
      return { type: 'hero', title: b.title, deck, body }
    }

    if (type === 'closing') {
      return { type: 'closing', title: b.title, body: b.raw.trim() }
    }

    // feature — may have ### subheadings
    const subs = b.raw.split(/\n###\s+/m)
    if (subs.length > 1) {
      const intro = subs[0].trim() || undefined
      const articles: SundayArticleItem[] = subs.slice(1).map(chunk => {
        const nl = chunk.indexOf('\n')
        const headline = nl === -1 ? chunk.trim() : chunk.slice(0, nl).trim()
        const articleBody = nl === -1 ? '' : chunk.slice(nl + 1).trim()
        return { headline, body: articleBody }
      })
      return { type: 'feature', title: b.title, intro, articles }
    }

    return { type: 'feature', title: b.title, body: b.raw.trim() }
  })

  // date_display: prefer "— X" suffix in title, else "Week NN"
  let date_display = `Week ${week}`
  const dashIdx = title.indexOf('—')
  if (dashIdx !== -1) {
    date_display = title.slice(dashIdx + 1).trim()
  }

  return { title, date_display, week_range, week, year, sections }
}

function renderSundayBody(body: string): string {
  if (!body) return ''
  const blocks = body.split(/\n\n+/).map(b => b.trim()).filter(Boolean)
  return blocks
    .map(block => {
      if (block.startsWith('>')) {
        // Blockquote → pullquote
        const qLines = block.split('\n').map(l => l.replace(/^>\s?/, '').trim())
        const attrLine = qLines[qLines.length - 1]
        const isAttr = attrLine.startsWith('—') || attrLine.startsWith('--')
        const quoteBody = isAttr ? qLines.slice(0, -1).join(' ') : qLines.join(' ')
        const attr = isAttr ? attrLine.replace(/^[-—]+\s*/, '') : ''
        return `<div class="pullquote"><p>${inlineMd(quoteBody)}</p>${
          attr ? `<div class="pullquote-attribution">${inlineMd(attr)}</div>` : ''
        }</div>`
      }
      return `<p>${inlineMd(block.replace(/\n/g, ' '))}</p>`
    })
    .join('')
}

/* ---------- Sunday Edition component ---------- */

function getISOWeek(d: Date): { year: number; week: number } {
  // ISO 8601 week-number calculation
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return { year: date.getUTCFullYear(), week: weekNo }
}

function sundayPath(year: number, week: number): string {
  const ww = String(week).padStart(2, '0')
  return `04 - Archive/Newspaper/Weekly/${year}-W${ww} - Sunday Edition.md`
}

function SundayEditionPage({ token }: { token: string | null }) {
  const [edition, setEdition] = useState<SundayEdition | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedWeek, setSelectedWeek] = useState<{ year: number; week: number } | null>(null)
  const [availableEditions, setAvailableEditions] = useState<Array<{ year: number; week: number }>>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      const now = new Date()
      let target = selectedWeek || getISOWeek(now)

      // Try to find a Sunday edition — walk back up to 8 weeks if current isn't available yet.
      const candidates: Array<{ year: number; week: number }> = []
      if (selectedWeek) {
        candidates.push(selectedWeek)
      } else {
        let { year, week } = target
        for (let i = 0; i < 9; i++) {
          candidates.push({ year, week })
          week -= 1
          if (week < 1) {
            year -= 1
            week = 52
          }
        }
      }

      // We also discover siblings opportunistically as we probe.
      const discovered: Array<{ year: number; week: number }> = []
      let loaded: { edition: SundayEdition; key: { year: number; week: number } } | null = null

      for (const c of candidates) {
        const path = sundayPath(c.year, c.week)
        try {
          const res = await fetch(
            `/api/vault/read?path=${encodeURIComponent(path).replace(/\+/g, '%2B')}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          )
          if (cancelled) return
          if (res.ok) {
            const data = await res.json()
            discovered.push(c)
            if (!loaded) {
              const parsed = parseSundayEdition(data.content || '', c.week, c.year)
              loaded = { edition: parsed, key: c }
            }
          }
        } catch {
          // ignore
        }
      }

      if (cancelled) return

      if (loaded) {
        setEdition(loaded.edition)
        setAvailableEditions(discovered)
        if (!selectedWeek) setSelectedWeek(loaded.key)
      } else {
        setError('No Sunday Edition found in the last 9 weeks.')
      }
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token, selectedWeek?.year, selectedWeek?.week])

  if (loading) {
    return (
      <div className="page-content active sunday-page">
        <div className="loading">Unfolding the Sunday Edition…</div>
      </div>
    )
  }

  if (error || !edition) {
    return (
      <div className="page-content active sunday-page">
        <div className="paper-empty">{error || 'No Sunday Edition available.'}</div>
      </div>
    )
  }

  const romanize = (n: number): string => {
    const romans: Array<[number, string]> = [
      [10, 'X'],
      [9, 'IX'],
      [5, 'V'],
      [4, 'IV'],
      [1, 'I'],
    ]
    let num = n
    let s = ''
    for (const [v, sym] of romans) {
      while (num >= v) {
        s += sym
        num -= v
      }
    }
    return s
  }

  return (
    <div className="page-content active sunday-page">
      <header className="sunday-masthead">
        <div className="sunday-ornament">✦ THE NERVOUS SYSTEM ✦</div>
        <div className="sunday-title">The Newspaper</div>
        <div className="sunday-subtitle">The Sunday Edition</div>
        {edition.date_display && (
          <div className="sunday-date">{edition.date_display}</div>
        )}
        {edition.week_range && (
          <div className="sunday-week-range">{edition.week_range}</div>
        )}
      </header>

      {availableEditions.length > 1 && (
        <nav className="edition-nav">
          {availableEditions.map(e => {
            const active = selectedWeek && e.year === selectedWeek.year && e.week === selectedWeek.week
            return (
              <button
                key={`${e.year}-W${e.week}`}
                className={`edition-link ${active ? 'active' : ''}`}
                onClick={() => setSelectedWeek(e)}
              >
                W{e.week}, {e.year}
              </button>
            )
          })}
        </nav>
      )}

      <div className="magazine">
        {edition.sections.length > 0 && (
          <div className="toc">
            <div className="toc-title">In This Edition</div>
            {edition.sections.map((s, i) => (
              <a
                key={`toc-${i}`}
                className="toc-item"
                href={`#sunday-sec-${i}`}
                onClick={e => {
                  e.preventDefault()
                  const el = document.getElementById(`sunday-sec-${i}`)
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                <span className="toc-section-num">{romanize(i + 1)}.</span>
                {s.title}
              </a>
            ))}
          </div>
        )}

        {edition.sections.map((s, i) => (
          <div id={`sunday-sec-${i}`} key={`sec-${i}`}>
            {s.type === 'hero' && (
              <section className="hero">
                <h1 className="hero-headline">{s.title}</h1>
                {s.deck && (
                  <div
                    className="hero-deck"
                    dangerouslySetInnerHTML={{ __html: inlineMd(s.deck) }}
                  />
                )}
                <div
                  className="hero-body"
                  dangerouslySetInnerHTML={{ __html: renderSundayBody(s.body) }}
                />
              </section>
            )}

            {s.type === 'stats' && (
              <section className="stats-box">
                <div className="stats-box-title">By the Numbers</div>
                <div className="stats-grid">
                  {s.stats.map((st, j) => (
                    <div className="stat-item" key={`st-${i}-${j}`}>
                      <div className="stat-value">{st.value}</div>
                      <div className="stat-label">{st.label}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {s.type === 'closing' && (
              <section className="closing">
                <h2 className="closing-headline">{s.title}</h2>
                <div
                  className="closing-body"
                  dangerouslySetInnerHTML={{ __html: renderSundayBody(s.body) }}
                />
              </section>
            )}

            {s.type === 'feature' && (
              <>
                <div className="section-divider">
                  <span className="section-divider-text">{s.title}</span>
                </div>
                {s.intro && (
                  <div
                    className="feature-body"
                    style={{ marginBottom: '1.25rem' }}
                    dangerouslySetInnerHTML={{ __html: renderSundayBody(s.intro) }}
                  />
                )}
                {s.articles && s.articles.length > 0
                  ? s.articles.map((a, j) => (
                      <article className="feature" key={`feat-${i}-${j}`}>
                        <h3 className="feature-headline">{a.headline}</h3>
                        <div
                          className="feature-body"
                          dangerouslySetInnerHTML={{ __html: renderSundayBody(a.body) }}
                        />
                      </article>
                    ))
                  : s.body && (
                      <article className="feature">
                        <div
                          className="feature-body"
                          dangerouslySetInnerHTML={{ __html: renderSundayBody(s.body) }}
                        />
                      </article>
                    )}
              </>
            )}
          </div>
        ))}
      </div>

      <footer className="sunday-footer">
        Published by Chronicle. The Sunday Edition. Brooklyn, NY. © The Nervous
        System, 2026.
      </footer>
    </div>
  )
}

/* ---------- scoped styles ---------- */

function PaperStyles() {
  return (
    <style>{`
/* Everything below is scoped under .paper-page so nothing can leak. */

.paper-page {
  --paper: #f4f1e8;
  --paper-light: #faf8f2;
  --ink: #1a1a1a;
  --ink-medium: #4a4a4a;
  --ink-light: #7a7a7a;
  --ink-lightest: #ccc;
  --rule: #c0b8a8;
  --rule-heavy: #1a1a1a;
  --accent-red: #8B0000;
  --decision-bg: #e8e5dc;
  --status-awake: #2a7d2a;
  --status-sleeping: #999999;
  --status-recent: #c49b2a;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Old Standard TT', 'Times New Roman', Georgia, serif;
  font-size: 0.95rem;
  line-height: 1.55;
  min-height: 100%;
  -webkit-font-smoothing: antialiased;
}
.paper-page *, .paper-page *::before, .paper-page *::after { box-sizing: border-box; }

/* Google Fonts are injected globally below, but the .paper-page scope confines their usage */

.paper-page .broadsheet {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.75rem 2rem 4rem;
}

/* === NAMEPLATE === */
.paper-page .nameplate {
  text-align: center;
  padding: 1rem 0 0.75rem;
  border-bottom: 3px solid var(--rule-heavy);
}
.paper-page .nameplate-ornament {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.8rem;
  color: var(--ink-light);
  letter-spacing: 0.5em;
  margin-bottom: 0.25rem;
}
.paper-page .nameplate-title {
  font-family: 'UnifrakturMaguntia', Georgia, serif;
  font-size: 3.6rem;
  line-height: 1.0;
  color: var(--ink);
  margin: 0.15rem 0;
  font-weight: 400;
}
.paper-page .nameplate-motto {
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.9rem;
  color: var(--ink-medium);
  margin-bottom: 0.4rem;
}
.paper-page .nameplate-meta {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.75rem;
  color: var(--ink-light);
  letter-spacing: 0.08em;
  display: flex;
  justify-content: space-between;
  margin-top: 0.3rem;
  text-transform: uppercase;
}

/* === TABS === */
.paper-page .page-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--rule);
  margin-top: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.paper-page .page-tab {
  font-family: 'Playfair Display', Georgia, serif;
  font-variant: small-caps;
  font-size: 0.88rem;
  letter-spacing: 0.12em;
  padding: 0.55rem 1.3rem;
  cursor: pointer;
  color: var(--ink-light);
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
  user-select: none;
}
.paper-page .page-tab:hover { color: var(--ink-medium); }
.paper-page .page-tab.active {
  color: var(--ink);
  border-bottom: 2px solid var(--rule-heavy);
}

.paper-page .page-content { display: block; }

/* === SECTION HEADERS === */
.paper-page .section-header {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--ink);
  border-bottom: 2px solid var(--rule-heavy);
  padding-bottom: 0.3rem;
  margin-bottom: 0.85rem;
  margin-top: 1.5rem;
}
.paper-page .section-header:first-child { margin-top: 0; }
.paper-page .section-header--breaking {
  border-top: 3px solid var(--accent-red);
  padding-top: 0.35rem;
}

/* === ARTICLES === */
.paper-page .article-headline {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 1.45rem;
  line-height: 1.2;
  color: var(--ink);
  margin-bottom: 0.35rem;
}
.paper-page .article-headline--banner {
  font-weight: 900;
  font-size: 2.4rem;
  line-height: 1.12;
  margin-bottom: 0.55rem;
}
.paper-page .article-headline--small { font-size: 1.15rem; }
.paper-page .article-date {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.72rem;
  color: var(--ink-light);
  letter-spacing: 0.06em;
  margin-bottom: 0.45rem;
  text-transform: uppercase;
}
.paper-page .article-body {
  font-family: 'Old Standard TT', serif;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--ink-medium);
}
.paper-page .article-body p { margin-bottom: 0.55rem; }
.paper-page .article-body strong { color: var(--ink); font-weight: 700; }
.paper-page .article-body em { font-style: italic; }
.paper-page .article-body a { color: var(--ink); text-decoration: underline; }
.paper-page .article-body code {
  font-family: 'JetBrains Mono', Menlo, monospace;
  font-size: 0.82em;
  background: rgba(0,0,0,0.04);
  padding: 1px 4px;
  border-radius: 2px;
}

/* === BANNER / COLUMNS === */
.paper-page .banner-article {
  border-bottom: 1px solid var(--rule);
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}
.paper-page .two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.75rem;
  border-bottom: 1px dotted var(--rule);
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}
.paper-page .two-col > *:first-child {
  border-right: 1px solid var(--rule);
  padding-right: 1.75rem;
}
.paper-page .three-col {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1.5rem;
}
.paper-page .three-col > * {
  border-right: 1px solid var(--rule);
  padding-right: 1.5rem;
}
.paper-page .three-col > *:last-child {
  border-right: none;
  padding-right: 0;
}
.paper-page .col-article { margin-bottom: 1rem; }
.paper-page .col-article + .col-article {
  border-top: 1px dotted var(--rule);
  padding-top: 0.75rem;
}

/* === RECORD LAYOUT === */
.paper-page .record-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}
.paper-page .record-layout > *:first-child {
  border-right: 1px solid var(--rule);
  padding-right: 2rem;
}

.paper-page .decisions-subtitle {
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.85rem;
  color: var(--ink-light);
  margin-bottom: 1rem;
  text-align: center;
}
.paper-page .decision-card {
  background: var(--decision-bg);
  border-left: 3px solid var(--ink-light);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  transition: border-color 0.2s;
}
.paper-page .decision-card:first-of-type {
  border-left: 4px solid var(--accent-red);
  background: linear-gradient(135deg, var(--decision-bg) 0%, rgba(139,0,0,0.03) 100%);
}
.paper-page .decision-text {
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--ink-medium);
}
.paper-page .decision-text strong { font-style: normal; color: var(--ink); }
.paper-page .decision-date {
  font-family: 'Playfair Display', Georgia, serif;
  font-variant: small-caps;
  font-size: 0.72rem;
  color: var(--ink-light);
  margin-top: 0.35rem;
}

/* === WORKING TOWARD === */
.paper-page .working-toward-overview {
  font-size: 0.92rem;
  color: var(--ink-medium);
  margin-bottom: 0.85rem;
  line-height: 1.5;
}
.paper-page .wt-tier { margin-bottom: 1rem; }
.paper-page .wt-tier-header {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink);
  border-bottom: 1px solid var(--ink-lightest);
  padding-bottom: 0.2rem;
  margin-bottom: 0.5rem;
}
.paper-page .wt-tier-subtitle {
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.8rem;
  color: var(--ink-light);
  margin-top: -0.3rem;
  margin-bottom: 0.4rem;
}
.paper-page .wt-card {
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.4rem;
  border-left: 3px solid var(--ink-lightest);
  background: rgba(0,0,0,0.015);
}
.paper-page .wt-card-name {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 0.92rem;
  color: var(--ink);
}
.paper-page .wt-card-desc {
  font-family: 'Old Standard TT', serif;
  font-size: 0.85rem;
  color: var(--ink-medium);
  line-height: 1.45;
  margin-top: 0.18rem;
}
.paper-page .wt-tier--building .wt-card { border-left-color: #2d5016; }
.paper-page .wt-tier--waiting .wt-card { border-left-color: #8B6914; }
.paper-page .wt-tier--paused .wt-card { border-left-color: var(--ink-light); }
.paper-page .wt-tier--incubating .wt-card { border-left-color: var(--ink-lightest); opacity: 0.85; }

/* === MASTHEAD / BULLPEN === */
.paper-page .masthead-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}
.paper-page .persona-card {
  background: var(--paper-light);
  border: 1px solid var(--rule);
  border-left: 3px solid var(--ink-light);
  padding: 0.55rem 0.65rem;
  font-size: 0.85rem;
}
.paper-page .persona-card-header {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  margin-bottom: 0.2rem;
}
.paper-page .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.paper-page .status-dot--awake { background: var(--status-awake); }
.paper-page .status-dot--sleeping { background: var(--status-sleeping); }
.paper-page .status-dot--recent { background: var(--status-recent); }
.paper-page .persona-name {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink);
}
.paper-page .persona-role {
  font-size: 0.72rem;
  color: var(--ink-light);
  margin-bottom: 0.2rem;
}
.paper-page .persona-card hr {
  border: none;
  border-top: 1px solid var(--rule);
  margin: 0.3rem 0;
}
.paper-page .persona-focus {
  font-style: italic;
  font-size: 0.75rem;
  color: var(--ink-medium);
}
.paper-page .persona-last {
  font-size: 0.7rem;
  color: var(--ink-light);
  margin-top: 0.15rem;
}

/* === INFRASTRUCTURE === */
.paper-page .infra-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.45rem;
  margin-bottom: 1.5rem;
}
.paper-page .infra-item {
  font-family: 'JetBrains Mono', Menlo, monospace;
  font-size: 0.76rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid var(--rule);
  background: var(--paper-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}
.paper-page .infra-name { display: flex; align-items: center; color: var(--ink); }
.paper-page .infra-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 0.35rem;
  background: var(--status-awake);
}
.paper-page .infra-status {
  font-family: 'Old Standard TT', serif;
  font-size: 0.76rem;
  color: var(--ink-light);
  text-align: right;
}

/* === FOOTER / STATES === */
.paper-page .broadsheet-footer {
  margin-top: 3rem;
  padding-top: 1rem;
  border-top: 1px solid var(--rule);
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.78rem;
  color: var(--ink-light);
  text-align: center;
}
.paper-page .paper-loading,
.paper-page .paper-error,
.paper-page .paper-empty {
  padding: 2rem 1rem;
  text-align: center;
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  color: var(--ink-light);
}
.paper-page .paper-error {
  color: var(--accent-red);
  font-style: normal;
  border: 1px solid var(--rule);
  background: var(--paper-light);
}
.paper-page .paper-error-detail {
  font-family: 'JetBrains Mono', Menlo, monospace;
  font-size: 0.72rem;
  color: var(--ink-light);
  margin-top: 0.5rem;
}
.paper-page .sunday-placeholder { min-height: 200px; }

/* === EDITIONS SIDEBAR === */
.paper-page .editions-toggle {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 20;
  font-family: 'Playfair Display', Georgia, serif;
  font-variant: small-caps;
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  color: var(--ink-light);
  background: var(--paper);
  border: 1px solid var(--rule);
  padding: 0.4rem 0.85rem;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}
.paper-page .editions-toggle:hover {
  color: var(--ink);
  border-color: var(--ink-light);
  background: var(--paper-light);
}
.paper-page .editions-panel {
  position: fixed;
  top: 0;
  left: -320px;
  width: 300px;
  height: 100vh;
  background: var(--paper-light);
  border-right: 1px solid var(--rule);
  padding: 3.5rem 1rem 1rem;
  overflow-y: auto;
  transition: left 0.25s ease-out;
  z-index: 19;
  box-shadow: 2px 0 8px rgba(0,0,0,0.04);
}
.paper-page .editions-panel.open { left: 0; }
.paper-page .editions-panel-title {
  font-family: 'Playfair Display SC', Georgia, serif;
  font-size: 0.85rem;
  letter-spacing: 0.2em;
  color: var(--ink-medium);
  padding-bottom: 0.4rem;
  border-bottom: 1px solid var(--rule);
  margin-bottom: 0.75rem;
}
.paper-page .edition-current {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 0.85rem;
  color: var(--ink-medium);
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  border-left: 3px solid transparent;
  margin-bottom: 0.5rem;
  transition: background 0.2s, border-color 0.2s;
}
.paper-page .edition-current:hover { background: var(--paper); }
.paper-page .edition-current.active {
  background: var(--decision-bg);
  border-left-color: var(--accent-red);
  color: var(--ink);
  font-weight: 700;
}
.paper-page .edition-date-group {
  font-family: 'Playfair Display SC', Georgia, serif;
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  color: var(--ink-light);
  margin: 0.85rem 0 0.3rem;
  padding-bottom: 0.15rem;
  border-bottom: 1px dotted var(--rule);
}
.paper-page .edition-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: 'Old Standard TT', serif;
  font-size: 0.85rem;
  color: var(--ink-medium);
  padding: 0.35rem 0.5rem;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: background 0.2s, border-color 0.2s;
}
.paper-page .edition-item:hover {
  background: var(--paper);
  border-left-color: var(--rule);
}
.paper-page .edition-item.active {
  background: var(--decision-bg);
  border-left-color: var(--ink);
  color: var(--ink);
  font-weight: 700;
}
.paper-page .edition-badge {
  font-family: 'Playfair Display', Georgia, serif;
  font-variant: small-caps;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
}
.paper-page .edition-time {
  font-family: 'JetBrains Mono', Menlo, monospace;
  font-size: 0.7rem;
  color: var(--ink-light);
}
.paper-page .edition-empty {
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.8rem;
  color: var(--ink-light);
  padding: 0.5rem;
}
.paper-page .edition-viewing-banner {
  position: sticky;
  top: 0;
  z-index: 18;
  background: var(--decision-bg);
  border-bottom: 2px solid var(--accent-red);
  padding: 0.5rem 1rem;
  font-family: 'Old Standard TT', serif;
  font-size: 0.85rem;
  color: var(--ink);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.paper-page .edition-viewing-banner .return-link {
  font-family: 'Playfair Display', Georgia, serif;
  font-variant: small-caps;
  font-size: 0.82rem;
  letter-spacing: 0.1em;
  color: var(--accent-red);
  background: transparent;
  border: 1px solid var(--accent-red);
  padding: 0.25rem 0.7rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.paper-page .edition-viewing-banner .return-link:hover {
  background: var(--accent-red);
  color: var(--paper-light);
}

/* === ARTICLE IMAGES === */
.paper-page .article-image {
  width: 100%;
  margin: 0.5rem 0 0.75rem;
  border: 1px solid var(--rule);
  display: block;
}
.paper-page .article-image--hero {
  margin: 0.75rem 0 1rem;
}
.paper-page .article-image--inline {
  width: 100%;
  margin: 0.5rem 0 0.75rem;
  border: 1px solid var(--rule);
}

/* === FRONT PAGE: see-all link === */
.paper-page .see-all-link {
  text-align: center;
  margin: 2rem 0 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--rule);
}
.paper-page .see-all-btn {
  font-family: 'Playfair Display', Georgia, serif;
  font-variant: small-caps;
  font-size: 0.95rem;
  letter-spacing: 0.1em;
  color: var(--ink);
  background: transparent;
  border: 1px solid var(--ink-light);
  padding: 0.55rem 1.4rem;
  cursor: pointer;
  transition: all 0.2s;
}
.paper-page .see-all-btn:hover {
  background: var(--decision-bg);
  border-color: var(--ink);
}

/* === THIS WEEK PAGE === */
.paper-page .tw-page { padding-top: 0.25rem; }
.paper-page .tw-masthead {
  text-align: center;
  padding: 1rem 0 1.25rem;
  border-bottom: 2px solid var(--rule-heavy);
  margin-bottom: 1.5rem;
}
.paper-page .tw-eyebrow {
  font-family: 'Playfair Display SC', Georgia, serif;
  font-size: 0.72rem;
  letter-spacing: 0.4em;
  color: var(--ink-light);
  margin-bottom: 0.35rem;
}
.paper-page .tw-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 900;
  font-size: 2.5rem;
  line-height: 1;
  color: var(--ink);
  margin: 0.15rem 0 0.35rem;
}
.paper-page .tw-meta {
  font-family: 'Old Standard TT', serif;
  font-style: italic;
  font-size: 0.82rem;
  color: var(--ink-light);
}
.paper-page .tw-group {
  margin-bottom: 2.5rem;
}
.paper-page .tw-group-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  border-bottom: 2px solid var(--rule-heavy);
  padding-bottom: 0.35rem;
  margin-bottom: 1rem;
}
.paper-page .tw-group-label {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink);
}
.paper-page .tw-group-count {
  font-family: 'Playfair Display SC', Georgia, serif;
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  color: var(--ink-light);
}
.paper-page .tw-group .banner-article {
  padding-bottom: 1.25rem;
  margin-bottom: 1.25rem;
}
.paper-page .tw-group .col-article {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px dotted var(--rule);
}
.paper-page .tw-group .col-article:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

/* === SUNDAY EDITION — magazine layout === */
.paper-page .sunday-page {
  /* Sunday has its own color palette — warmer, grander. */
  --s-paper: #FAF8F2;
  --s-paper-warm: #F5F0E4;
  --s-paper-cream: #FFFDF5;
  --s-ink: #1a1a1a;
  --s-ink-medium: #3d3d3d;
  --s-ink-light: #6a6a6a;
  --s-rule: #d4c9b4;
  --s-rule-heavy: #1a1a1a;
  --s-accent: #8B0000;
  --s-accent-warm: #A0522D;
  --s-section-bg: #F0EBE0;
  --s-pullquote-border: #C19A6B;
  background: var(--s-paper);
  color: var(--s-ink);
  font-family: 'Cormorant Garamond', 'Georgia', serif;
  font-size: 1.05rem;
  line-height: 1.7;
  padding: 0;
  margin: 0 -2rem;
}

.paper-page .sunday-masthead {
  text-align: center;
  padding: 2.5rem 2rem 1.5rem;
  border-bottom: 4px double var(--s-rule-heavy);
  background: var(--s-paper-cream);
}
.paper-page .sunday-ornament {
  font-family: 'Playfair Display SC', Georgia, serif;
  font-size: 0.75rem;
  letter-spacing: 0.6em;
  color: var(--s-ink-light);
  margin-bottom: 0.5rem;
}
.paper-page .sunday-title {
  font-family: 'UnifrakturMaguntia', Georgia, serif;
  font-size: 4rem;
  line-height: 1;
  color: var(--s-ink);
  margin: 0.3rem 0;
}
.paper-page .sunday-subtitle {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 400;
  font-style: italic;
  font-size: 1.3rem;
  color: var(--s-accent-warm);
  letter-spacing: 0.05em;
  margin: 0.3rem 0;
}
.paper-page .sunday-date {
  font-family: 'Playfair Display SC', Georgia, serif;
  font-size: 0.85rem;
  color: var(--s-ink-light);
  letter-spacing: 0.15em;
  margin-top: 0.5rem;
}
.paper-page .sunday-week-range {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 0.95rem;
  color: var(--s-ink-light);
}
.paper-page .sunday-page .magazine {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}
.paper-page .sunday-page .loading {
  text-align: center;
  font-style: italic;
  color: var(--s-ink-light);
  padding: 3rem;
  font-size: 1.1rem;
}
.paper-page .sunday-page .edition-nav {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin: 2rem 0;
  flex-wrap: wrap;
}
.paper-page .sunday-page .edition-link {
  font-family: 'Playfair Display', serif;
  font-size: 0.8rem;
  color: var(--s-ink-light);
  text-decoration: none;
  padding: 0.3rem 0.75rem;
  border: 1px solid var(--s-rule);
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}
.paper-page .sunday-page .edition-link:hover,
.paper-page .sunday-page .edition-link.active {
  color: var(--s-ink);
  border-color: var(--s-ink);
  background: var(--s-paper-warm);
}

.paper-page .sunday-page .toc {
  background: var(--s-section-bg);
  border: 1px solid var(--s-rule);
  padding: 1.5rem 2rem;
  margin: 2rem 0;
}
.paper-page .sunday-page .toc-title {
  font-family: 'Playfair Display SC', serif;
  font-size: 0.85rem;
  letter-spacing: 0.2em;
  color: var(--s-ink-medium);
  margin-bottom: 0.75rem;
  border-bottom: 1px solid var(--s-rule);
  padding-bottom: 0.4rem;
}
.paper-page .sunday-page .toc-item {
  display: block;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.05rem;
  color: var(--s-ink-medium);
  text-decoration: none;
  padding: 0.3rem 0;
  border-bottom: 1px dotted var(--s-rule);
  transition: color 0.2s;
  cursor: pointer;
}
.paper-page .sunday-page .toc-item:last-child { border-bottom: none; }
.paper-page .sunday-page .toc-item:hover { color: var(--s-accent); }
.paper-page .sunday-page .toc-section-num {
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  font-size: 0.8rem;
  color: var(--s-accent-warm);
  margin-right: 0.5rem;
}

.paper-page .sunday-page .hero {
  margin: 2.5rem 0;
  padding-bottom: 2rem;
  border-bottom: 2px solid var(--s-rule);
}
.paper-page .sunday-page .hero-headline {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 900;
  font-size: 2.8rem;
  line-height: 1.1;
  color: var(--s-ink);
  margin-bottom: 0.5rem;
}
.paper-page .sunday-page .hero-deck {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 1.3rem;
  color: var(--s-ink-medium);
  line-height: 1.5;
  margin-bottom: 1.5rem;
  max-width: 700px;
}
.paper-page .sunday-page .hero-body {
  font-size: 1.1rem;
  line-height: 1.8;
  column-count: 2;
  column-gap: 2.5rem;
  column-rule: 1px solid var(--s-rule);
}
.paper-page .sunday-page .hero-body p { margin-bottom: 0.8rem; }
.paper-page .sunday-page .hero-body p:first-of-type::first-letter {
  font-family: 'Playfair Display', serif;
  font-weight: 900;
  font-size: 4rem;
  float: left;
  line-height: 0.8;
  padding-right: 0.5rem;
  padding-top: 0.2rem;
  color: var(--s-accent);
}

.paper-page .sunday-page .section-divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 2.5rem 0 1.5rem;
}
.paper-page .sunday-page .section-divider::before,
.paper-page .sunday-page .section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--s-rule);
}
.paper-page .sunday-page .section-divider-text {
  font-family: 'Playfair Display SC', serif;
  font-size: 0.85rem;
  letter-spacing: 0.2em;
  color: var(--s-ink-light);
  white-space: nowrap;
}

.paper-page .sunday-page .feature {
  margin: 2rem 0;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--s-rule);
}
.paper-page .sunday-page .feature:last-child { border-bottom: none; }
.paper-page .sunday-page .feature-headline {
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  font-size: 1.6rem;
  line-height: 1.2;
  color: var(--s-ink);
  margin-bottom: 0.5rem;
}
.paper-page .sunday-page .feature-body {
  font-size: 1.05rem;
  line-height: 1.75;
}
.paper-page .sunday-page .feature-body p { margin-bottom: 0.7rem; }
.paper-page .sunday-page .feature-body strong { color: var(--s-ink); }

.paper-page .sunday-page .pullquote {
  border-left: 4px solid var(--s-pullquote-border);
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  background: var(--s-section-bg);
}
.paper-page .sunday-page .pullquote p {
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: 1.25rem;
  line-height: 1.5;
  color: var(--s-ink-medium);
}
.paper-page .sunday-page .pullquote-attribution {
  font-family: 'Playfair Display SC', serif;
  font-size: 0.8rem;
  color: var(--s-ink-light);
  letter-spacing: 0.1em;
  margin-top: 0.5rem;
}

.paper-page .sunday-page .stats-box {
  background: var(--s-paper-warm);
  border: 1px solid var(--s-rule);
  padding: 1.5rem 2rem;
  margin: 2rem 0;
}
.paper-page .sunday-page .stats-box-title {
  font-family: 'Playfair Display SC', serif;
  font-size: 0.85rem;
  letter-spacing: 0.2em;
  color: var(--s-ink-medium);
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--s-rule);
  padding-bottom: 0.4rem;
}
.paper-page .sunday-page .stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}
.paper-page .sunday-page .stat-item {
  text-align: center;
  padding: 0.5rem;
}
.paper-page .sunday-page .stat-value {
  font-family: 'Playfair Display', serif;
  font-weight: 900;
  font-size: 2rem;
  color: var(--s-accent);
  line-height: 1.1;
}
.paper-page .sunday-page .stat-label {
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.85rem;
  color: var(--s-ink-light);
  margin-top: 0.2rem;
}

.paper-page .sunday-page .closing {
  background: var(--s-section-bg);
  border-top: 3px solid var(--s-rule-heavy);
  border-bottom: 3px solid var(--s-rule-heavy);
  padding: 2rem 2.5rem;
  margin: 2.5rem 0;
  text-align: center;
}
.paper-page .sunday-page .closing-headline {
  font-family: 'Playfair Display', serif;
  font-weight: 900;
  font-size: 1.8rem;
  line-height: 1.2;
  color: var(--s-ink);
  margin-bottom: 1rem;
}
.paper-page .sunday-page .closing-body {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.15rem;
  line-height: 1.7;
  color: var(--s-ink-medium);
  max-width: 650px;
  margin: 0 auto;
}
.paper-page .sunday-page .closing-body p { margin-bottom: 0.5rem; }

.paper-page .sunday-footer {
  text-align: center;
  padding: 2rem;
  font-family: 'Playfair Display', serif;
  font-size: 0.75rem;
  color: var(--s-ink-light);
  letter-spacing: 0.1em;
  border-top: 1px solid var(--s-rule);
  margin-top: 2rem;
}

@media (max-width: 700px) {
  .paper-page .sunday-title { font-size: 2.8rem; }
  .paper-page .sunday-page .hero-headline { font-size: 2rem; }
  .paper-page .sunday-page .hero-body { column-count: 1; }
  .paper-page .sunday-page .magazine { padding: 0 1rem; }
  .paper-page .sunday-page .stats-grid { grid-template-columns: repeat(2, 1fr); }
}

/* === RESPONSIVE === */
@media (max-width: 900px) {
  .paper-page .three-col { grid-template-columns: 1fr 1fr; }
  .paper-page .three-col > *:nth-child(2) { border-right: none; padding-right: 0; }
}
@media (max-width: 640px) {
  .paper-page .broadsheet { padding: 1.25rem 1rem 3rem; }
  .paper-page .nameplate-title { font-size: 2.6rem; }
  .paper-page .two-col { grid-template-columns: 1fr; }
  .paper-page .two-col > *:first-child { border-right: none; padding-right: 0; }
  .paper-page .three-col { grid-template-columns: 1fr; }
  .paper-page .three-col > * { border-right: none; padding-right: 0; }
  .paper-page .record-layout { grid-template-columns: 1fr; }
  .paper-page .record-layout > *:first-child { border-right: none; padding-right: 0; padding-bottom: 1rem; margin-bottom: 1rem; border-bottom: 1px solid var(--rule); }
  .paper-page .page-tab { padding: 0.5rem 0.85rem; font-size: 0.78rem; }
  .paper-page .nameplate-meta { flex-direction: column; gap: 0.15rem; align-items: center; }
}

/* Google Fonts are loaded via usePaperFonts() hook, which injects a single <link> to document.head */
    `}</style>
  )
}
